import React, { useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  ListItem,
  ListItemText,
  Box,
  TextField,
  MenuItem,
  Link
} from '@material-ui/core';

import MaterialTable from 'material-table';

import CssList from './CssList';
import PrintPdfDialog from '../PrintPdfDialog';
import PrintLbxHtmlDialog from '../PrintLbxHtmlDialog';
import WarehouseDialog from '../WarehouseDialog';
import { updateCurrentDeviceRequest, getDeviceRequest } from '../../reducers/devices';
import NotyHelper from '../../api/NotyHelper';

const API_URL = process.env.REACT_APP_API_URL;

const PrintDialog = ({
  isPrintDialogOpen,
  deviceId,
  setIsPrintDialogOpen,
  classes,
  printTemplates
}) => (
  <Formik
    initialValues={{
      printTemplate: ''
    }}
    onSubmit={async (values, { setSubmitting }) => {
      try {
        const token = JSON.parse(
          JSON.parse(localStorage.getItem('persist:root')).auth
        ).accessToken;
        const response = await fetch(`${API_URL}devices/print/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            device_id: deviceId,
            template_id: values.printTemplate
          }),
          redirect: 'follow'
        });
        if (!response.ok) {
          throw new Error('Error uploading file');
        }

        const disposition = response.headers.get('Content-Disposition');
        let filename;

        if (disposition && disposition.includes('filename')) {
          const filenameIndex = disposition.indexOf('filename=');
          if (filenameIndex !== -1) {
            let rawFilename = disposition.substring(filenameIndex + 9);

            rawFilename = rawFilename.split(';')[0];
            filename = rawFilename.replace(/['"]/g, '').trim();
          }
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        setIsPrintDialogOpen(false);
      } catch (error) {
        console.error('Print error:', error);
        alert('Failed to generate file for printing');
      } finally {
        setSubmitting(false);
      }
    }}
    validationSchema={Yup.object().shape({
      printTemplate: Yup.string().required('Print template is required')
    })}
  >
    {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
      <Dialog
        open={isPrintDialogOpen}
        onClose={(_, reason) => {
          if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          setIsPrintDialogOpen(false);
        }}
      >
        <DialogContent>
          <form onSubmit={handleSubmit} autoComplete="off">
            <TextField
              id="select-print-template"
              select
              label="Print template"
              variant="outlined"
              className={classes.formControl}
              name="printTemplate"
              value={values.printTemplate}
              onChange={handleChange}
              disabled={isSubmitting}
              error={touched.printTemplate && Boolean(errors.printTemplate)}
              helperText={touched.printTemplate && errors.printTemplate}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {printTemplates.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}{' '}
                  {option.description ? `(${option.description})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPrintDialogOpen(false)} color="primary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            autoFocus
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmitting ? 'Printing…' : 'Ok'}
          </Button>
        </DialogActions>
      </Dialog>
    )}
  </Formik>
);

const DeviceInfoGrid = ({
  device,
  deviceInfo,
  classes,
  colors,
  deviceId,
  dispatch,
  printTemplates,
  printTemplateGroups,
  sku,
  productLines,
  roles,
  brands,
  cases,
  powers,
  hws,
  modelTypes,
  refreshSku
}) => {
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isPrintPdfDialogOpen, setIsPrintPdfDialogOpen] = useState(false);
  const [isPrintLbxHtmlDialogOpen, setIsPrintLbxHtmlDialogOpen] = useState(false);
  const [isWarehouseDialogOpen, setIsWarehouseDialogOpen] = useState(false);
  const [isSKUDialogOpen, setIsSKUDialogOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedSkuId, setSelectedSkuId] = useState(device.sku);
  // { open, field: 'brand' | 'model_type', pendingValue } — confirm before
  // changing fields that trigger ext_serial_number regeneration on the backend.
  const [regenConfirm, setRegenConfirm] = useState({ open: false, field: null, pendingValue: null });
  // Formik's setFieldValue, captured during render so the SKU modal -- which sits
  // outside the form -- can write the picked code into the field.
  const skuFieldSetter = useRef(null);
  const ipAdresses = deviceInfo?.network_interfaces?.filter(obj => obj.ip && obj.ip.trim() !== '')

  useEffect(() => {
    setSelectedSkuId(device.sku);
  }, [device.sku])

  useEffect(() => {
    if (!sku || sku.length === 0) return;
    setSelectedSku(
      sku.reduce((acc, curval) => {
        return curval.id === selectedSkuId ? curval : acc;
      }, null)
    );
  }, [selectedSkuId, sku]);

  return (
    <Grid item className={classes.listInfo}>
      <React.Fragment>
        <CssList dense>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ListItem>
                <ListItemText
                  primary="External serial number"
                  secondary={device.ext_serial_number}
                />
              </ListItem>
            </Grid>
            <Grid item xs={4}>
              <ListItem>
                <ListItemText
                  primary="Initialized"
                  secondary={
                    device.init
                      ? `Yes, ${moment(device.init_date).format(
                          'DD.MM.YYYY HH:mm'
                        )}`
                      : 'No'
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Device ID"
                  secondary={device.device_id}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Warehouse"
                  secondary={
                    device.warehouse_sent_at
                      ? `${device.portal_warehouse_name ||
                          device.portal_warehouse_code ||
                          '—'} · ${moment(device.warehouse_sent_at).format(
                          'DD.MM.YYYY HH:mm'
                        )}`
                      : 'Not sent'
                  }
                />
              </ListItem>
            </Grid>
            <Grid item xs={4}>
              <ListItem>
                <ListItemText primary="Model" secondary={device.model} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Disk size"
                  secondary={`${Math.floor(device.disk_size / 1000 / 1000 / 1000)} GB`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Mac addresses"
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={device.mac_addresses && device.mac_addresses.length >0 ? device.mac_addresses.map((el, ind) => {
                    return (
                      <div key={ind}>
                        MAC-{ind + 1}: {el}
                      </div>
                    );
                  }) : 'No MAC address'}
                />
              </ListItem>
                <ListItem>
                <ListItemText
                  primary="IP addresses"
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={ipAdresses && ipAdresses.length > 0 ? ipAdresses.map((el, ind) => {
                    return (
                      <div key={ind}>
                        IP-{ind + 1}: {el.ip}
                      </div>
                    );
                  }) : 'No IP address'}
                />
              </ListItem>
            </Grid>
            {deviceInfo ? (
              <Grid item xs={4}>
                <React.Fragment>
                  <ListItem>
                    <ListItemText
                      primary="Release"
                      secondary={deviceInfo.release}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Version"
                      secondary={deviceInfo.version}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Machine"
                      secondary={deviceInfo.machine}
                    />
                  </ListItem>
                </React.Fragment>
              </Grid>
            ) : null}
          </Grid>
        </CssList>
        <Formik
          enableReinitialize
          initialValues={{
            color: device.color || '',
            manual_sku: device.manual_sku || '',
            manual_bar_code: device.manual_bar_code || '',
            comment: device.comment || '',
            ext_serial_number: device.ext_serial_number || '',
            product_line: device.product_line || '',
            role: device.role || '',
            brand: brands && brands.length > 0
              ? (brands.some(c => c.id === device.brand) ? device.brand : brands[0].id)
              : (device.brand || 1),
            case: device.case || '',
            power: device.power || '',
            hw: device.hw || '',
            model_type: device.model_type || ''
          }}
          onSubmit={(values, { setSubmitting, setErrors }) => {
            const cleanValues = {
              ...values,
              sku: selectedSkuId,
              manual_sku: (values.manual_sku || '').trim(),
              manual_bar_code: (values.manual_bar_code || '').trim(),
              product_line: values.product_line === '' ? null : values.product_line,
              role: values.role === '' ? null : values.role,
              case: values.case === '' ? null : values.case,
              power: values.power === '' ? null : values.power,
              hw: values.hw === '' ? null : values.hw,
              model_type: values.model_type === '' ? null : values.model_type,
            };

            updateCurrentDeviceRequest(
              dispatch,
              deviceId,
              cleanValues,
              () => {
                setSubmitting(false);
                NotyHelper.success('Saved successfully');
              },
              (fieldErrors) => {
                setSubmitting(false);
                reportFieldErrors(fieldErrors, setErrors);
              }
            );
          }}
          validationSchema={Yup.object().shape({
            ext_serial_number: Yup.string()
              .max(20, 'Length must be less than 20 characters')
              .nullable(),
            // The backend refuses anything else, and its error never reaches the
            // form: the save handler turns every 4xx into 'Server error', so
            // without this the operator is told nothing about which field is wrong.
            manual_bar_code: Yup.string()
              .matches(/^(\d{12}|\d{13})?$/, 'EAN-13: 12 digits, or 13 with the check digit')
              .nullable()
          })}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setErrors,
            dirty,
            initialValues
          }) => {
            // The modal lives outside this form, so it hands the picked code back
            // through here: the field shows it at once instead of only after a save.
            skuFieldSetter.current = setFieldValue;

            // Marks the individual fields an operator has edited but not saved, so
            // the warning above the buttons points at something concrete.
            const unsaved = (name) => (values[name] !== initialValues[name]
              ? { InputProps: { style: { backgroundColor: UNSAVED_BACKGROUND } } }
              : {});

            const formatDataForServer = (currentValues) => {
              return {
                ...currentValues,
                sku: selectedSkuId,
                manual_sku: (currentValues.manual_sku || '').trim(),
                manual_bar_code: (currentValues.manual_bar_code || '').trim(),
                product_line: currentValues.product_line === '' ? null : currentValues.product_line,
                role: currentValues.role === '' ? null : currentValues.role,
                case: currentValues.case === '' ? null : currentValues.case,
                power: currentValues.power === '' ? null : currentValues.power,
                hw: currentValues.hw === '' ? null : currentValues.hw,
                model_type: currentValues.model_type === '' ? null : currentValues.model_type,
              };
            };

            const handleAutoSave = (name, value) => {
              setFieldValue(name, value);

              const updatedValues = { ...values, [name]: value };
              const dataToSave = formatDataForServer(updatedValues);

              updateCurrentDeviceRequest(
                dispatch,
                deviceId,
                dataToSave,
                () => NotyHelper.success('Device updated successfully'),
                (fieldErrors) => reportFieldErrors(fieldErrors, setErrors)
              );
            };

            // Brand / model_type changes trigger ext_serial_number regeneration on
            // the backend. Warn the operator before applying.
            const requestRegenConfirm = (field, newValue) => {
              if (newValue === values[field]) return;
              if (device.ext_serial_number) {
                setRegenConfirm({ open: true, field, pendingValue: newValue });
                return;
              }
              handleAutoSave(field, newValue);
            };

            const confirmRegen = () => {
              if (regenConfirm.field) {
                handleAutoSave(regenConfirm.field, regenConfirm.pendingValue);
              }
              setRegenConfirm({ open: false, field: null, pendingValue: null });
            };

            const cancelRegen = () => {
              setRegenConfirm({ open: false, field: null, pendingValue: null });
            };
            return (
              <React.Fragment>
              <form onSubmit={handleSubmit} autoComplete="off">
                <label>SKU</label>
                <br />
                <Link
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (refreshSku) refreshSku();
                    setIsSKUDialogOpen(true);
                  }}
                >
                  {selectedSku !== null ? selectedSku.sku : 'Select SKU'}
                </Link>
                {selectedSku !== null ? (
                  <Button
                    style={{ marginLeft: '10px' }}
                    onClick={() => {
                      setSelectedSkuId(null);
                    }}
                    variant="contained"
                    color="primary"
                  >
                    Clear
                  </Button>
                ) : null}
                <br />
                <br />
                {/* The code this device carries. Picking a row above fills it in;
                    a device whose SKU is not in the table has it typed here. The
                    table itself is only edited in the admin. */}
                <TextField
                  id="manual-sku"
                  label="SKU manually"
                  variant="outlined"
                  className={classes.formControl}
                  name="manual_sku"
                  {...unsaved('manual_sku')}
                  value={values.manual_sku}
                  onChange={handleChange}
                  helperText="Filled in when a SKU is picked above; can be typed by hand"
                  error={Boolean(errors.manual_sku && touched.manual_sku)}
                />
                <br />
                {/* Without it a hand-typed SKU prints a label with no barcode: the
                    code and the bar code come as a pair from the table, and a
                    device outside the table has to be given both. */}
                <TextField
                  id="manual-bar-code"
                  label="Bar code manually"
                  variant="outlined"
                  className={classes.formControl}
                  name="manual_bar_code"
                  {...unsaved('manual_bar_code')}
                  value={values.manual_bar_code}
                  onChange={handleChange}
                  helperText="EAN-13: 12 digits, or 13 with the check digit"
                  error={Boolean(errors.manual_bar_code && touched.manual_bar_code)}
                />
                <br />
                <br />
                {brands && brands.length > 0 && (
                  <>
                    <TextField
                      id="select-brand"
                      select
                      label="Brand"
                      variant="outlined"
                      className={classes.formControl}
                      name="brand"
                      {...unsaved('brand')}
                      value={values.brand}
                      onChange={(e) => requestRegenConfirm('brand', e.target.value)}
                    >
                      {brands.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <br />
                  </>
                )}

                {/* Model type is aarch64-only (Zeus / Zeus Lite / Rapid family) —
                    hide for x86_64 (Spartan) and other architectures. */}
                {deviceInfo?.machine === 'aarch64' && (
                  <>
                    <TextField
                      id="select-model-type"
                      select
                      label="Model type"
                      variant="outlined"
                      className={classes.formControl}
                      name="model_type"
                      {...unsaved('model_type')}
                      value={modelTypes && modelTypes.some(c => c.id === values.model_type) ? values.model_type : ''}
                      onChange={(e) => requestRegenConfirm('model_type', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {modelTypes && modelTypes.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.code} ({option.name})
                        </MenuItem>
                      ))}
                    </TextField>
                    <br />
                  </>
                )}

                <TextField
                  label="External serial number"
                  variant="outlined"
                  className={classes.formControl}
                  name="ext_serial_number"
                  {...unsaved('ext_serial_number')}
                  value={values.ext_serial_number}
                  length={3}
                  error={
                    touched.ext_serial_number && Boolean(errors.ext_serial_number)
                  }
                  helperText={
                    touched.ext_serial_number && errors.ext_serial_number
                  }
                  onChange={handleChange}
                />
                <br />

                <TextField
                  id="select-product-line"
                  select
                  label="Product line"
                  variant="outlined"
                  className={classes.formControl}
                  name="product_line"
                  {...unsaved('product_line')}
                  value={productLines && productLines.some(c => c.id === values.product_line) ? values.product_line : ''}
                  onChange={(e) => handleAutoSave('product_line', e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {productLines && productLines.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                <br />

                <TextField
                  id="select-role"
                  select
                  label="Role"
                  variant="outlined"
                  className={classes.formControl}
                  name="role"
                  {...unsaved('role')}
                  value={roles && roles.some(c => c.id === values.role) ? values.role : ''}
                  onChange={(e) => handleAutoSave('role', e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {roles && roles.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                <br />

                {/* Case is aarch64-only (Zeus / Lite / Rapid). */}
                {deviceInfo?.machine === 'aarch64' && (
                  <>
                    <TextField
                      id="select-case"
                      select
                      label="Case"
                      variant="outlined"
                      className={classes.formControl}
                      name="case"
                      {...unsaved('case')}
                      value={cases && cases.some(c => c.id === values.case) ? values.case : ''}
                      onChange={(e) => handleAutoSave('case', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {cases && cases.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.code} ({option.name})
                        </MenuItem>
                      ))}
                    </TextField>
                    <br />
                  </>
                )}

                <TextField
                  id="select-color"
                  select
                  label="Color"
                  variant="outlined"
                  className={classes.formControl}
                  name="color"
                  {...unsaved('color')}
                  value={colors && colors.some(c => c.id === values.color) ? values.color : ''}
                  onChange={(e) => handleAutoSave('color', e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {colors.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.color}
                    </MenuItem>
                  ))}
                </TextField>
                <br />

                {/* HW and Power are aarch64-only (Zeus / Lite / Rapid). */}
                {deviceInfo?.machine === 'aarch64' && (
                  <>
                    <TextField
                      id="select-hw"
                      select
                      label="HW"
                      variant="outlined"
                      className={classes.formControl}
                      name="hw"
                      {...unsaved('hw')}
                      value={hws && hws.some(c => c.id === values.hw) ? values.hw : ''}
                      onChange={(e) => handleAutoSave('hw', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {hws && hws.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.code} ({option.name})
                        </MenuItem>
                      ))}
                    </TextField>
                    <br />

                    <TextField
                      id="select-power"
                      select
                      label="Power"
                      variant="outlined"
                      className={classes.formControl}
                      name="power"
                      {...unsaved('power')}
                      value={powers && powers.some(c => c.id === values.power) ? values.power : ''}
                      onChange={(e) => handleAutoSave('power', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {powers && powers.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.code} ({option.name})
                        </MenuItem>
                      ))}
                    </TextField>
                    <br />
                  </>
                )}

                <TextField
                  label="Comment (optional)"
                  multiline
                  rows={2}
                  variant="outlined"
                  className={classes.formControl}
                  name="comment"
                  {...unsaved('comment')}
                  value={values.comment}
                  onChange={handleChange}
                />
                <br />

                {/* Nothing on this card saves by itself, and an operator who walks
                    off to the printer with edits still in the form prints the old
                    values. Warned three ways: the edited fields are tinted, the
                    button says so, and the browser asks on the way out. */}
                <UnsavedChangesGuard active={dirty && !isSubmitting} />

                <Box my={1} display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
                  <Button
                    variant="contained"
                    color={dirty ? 'secondary' : 'primary'}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {/* the label stays put: a wider word would widen the row and
                        move everything again */}
                    Save
                  </Button>{' '}
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (!device.init) {
                        NotyHelper.error(
                          'The device has not initialized yet. Please initialize it before printing.'
                        );
                        return;
                      }
                      setIsPrintDialogOpen(true);
                    }}
                  >
                    Print LBX
                  </Button>{' '}
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (!device.init) {
                        NotyHelper.error(
                          'The device has not initialized yet. Please initialize it before printing.'
                        );
                        return;
                      }
                      setIsPrintPdfDialogOpen(true);
                    }}
                  >
                    Print PDF
                  </Button>{' '}
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (!device.init) {
                        NotyHelper.error(
                          'The device has not initialized yet. Please initialize it before printing.'
                        );
                        return;
                      }
                      setIsPrintLbxHtmlDialogOpen(true);
                    }}
                  >
                    Print LBX (HTML)
                  </Button>{' '}
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={!device.init}
                    onClick={() => setIsWarehouseDialogOpen(true)}
                  >
                    Send to warehouse
                  </Button>
                  {/* Always rendered, only shown when there is something to say:
                      appearing and disappearing would change the width of this row,
                      and the whole card's grid reflows behind it -- the columns up
                      top visibly jump the moment an operator types. */}
                  <Box
                    component="span"
                    px={1}
                    py={0.5}
                    style={{
                      border: '1px solid #f0ad4e',
                      borderRadius: 4,
                      backgroundColor: UNSAVED_BACKGROUND,
                      whiteSpace: 'nowrap',
                      visibility: dirty && !isSubmitting ? 'visible' : 'hidden'
                    }}
                  >
                    Unsaved changes — printing uses the stored values
                  </Box>
                </Box>
              </form>
              <Dialog open={regenConfirm.open} onClose={cancelRegen}>
                <DialogTitle>
                  {regenConfirm.field === 'brand' ? 'Change brand?' : 'Change model type?'}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Changing the {regenConfirm.field === 'brand' ? 'brand' : 'model type'} will
                    regenerate the External serial number for this device. This action cannot be
                    undone.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={cancelRegen} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={confirmRegen} color="primary" autoFocus>
                    Change
                  </Button>
                </DialogActions>
              </Dialog>
            </React.Fragment>
            )
          }}
        </Formik>
      </React.Fragment>
      <PrintDialog
        isPrintDialogOpen={isPrintDialogOpen}
        deviceId={deviceId}
        setIsPrintDialogOpen={setIsPrintDialogOpen}
        classes={classes}
        printTemplates={printTemplates}
      />
      <PrintPdfDialog
        open={isPrintPdfDialogOpen}
        onClose={() => setIsPrintPdfDialogOpen(false)}
        deviceIds={[deviceId]}
        printTemplateGroups={printTemplateGroups}
        defaultFilename="device.pdf"
      />
      <PrintLbxHtmlDialog
        open={isPrintLbxHtmlDialogOpen}
        onClose={() => setIsPrintLbxHtmlDialogOpen(false)}
        deviceId={deviceId}
        printTemplateGroups={printTemplateGroups}
        defaultFilename="device.zip"
      />
      <WarehouseDialog
        open={isWarehouseDialogOpen}
        onClose={() => setIsWarehouseDialogOpen(false)}
        deviceId={deviceId}
        onSuccess={() => getDeviceRequest(deviceId)}
      />
      <SKUTableModal
        isOpen={isSKUDialogOpen}
        setIsOpen={setIsSKUDialogOpen}
        sku={sku}
        setSelectedSkuId={setSelectedSkuId}
        setManualSku={(code, barCode) => {
          if (!skuFieldSetter.current) return;
          skuFieldSetter.current('manual_sku', code);
          skuFieldSetter.current('manual_bar_code', barCode || '');
        }}
      />
    </Grid>
  );
};

// Warns while the form holds edits that have not reached the server: a line the
// operator cannot miss, and a browser prompt if they try to leave the page. Printing
// reads what is stored, so unsaved edits would silently print the previous values.
// The same yellow the banner uses, so an edited field and the warning read as one
// thing rather than two unrelated colours.
const UNSAVED_BACKGROUND = '#fff8e1';

const UnsavedChangesGuard = ({ active }) => {
  useEffect(() => {
    if (!active) return undefined;

    const warn = (event) => {
      event.preventDefault();
      // every browser shows its own wording; a non-empty value is what triggers it
      event.returnValue = 'The device has unsaved changes.';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);

  // Renders nothing: the warning an operator sees is the tinted fields and the
  // button label. Anything drawn here would reflow the page the moment they type
  // and push the buttons out of sight.
  return null;
};

// What the API rejected the request with -- { field: [message] } -- put where the
// operator can act on it: under the field itself, and in the notification, instead
// of the bare 'Server error' that used to be all they saw.
const reportFieldErrors = (fieldErrors, setErrors) => {
  const named = fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)
    ? fieldErrors
    : null;

  if (!named || Object.keys(named).length === 0) {
    NotyHelper.error('Server error');
    return;
  }

  const messages = {};
  Object.keys(named).forEach((field) => {
    const value = named[field];
    messages[field] = Array.isArray(value) ? value.join(' ') : String(value);
  });

  setErrors(messages);
  NotyHelper.error(Object.keys(messages).map(f => `${f}: ${messages[f]}`).join('\n'));
};

const SKUTableModal = ({ isOpen, setIsOpen, sku, setSelectedSkuId, setManualSku }) => {
  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={'md'}
      onClose={() => setIsOpen(false)}
    >
      <MaterialTable
        className
        // actions={[
        //   {
        //     icon: 'edit',
        //     tooltip: 'Edit',
        //     onClick: (event, rowData) => {
        //       // history.push(`/device/offline/${rowData.device_id}`);
        //     }
        //   }
        // ]}
        // components={{
        //   Row: props => <MTableBodyRow {...props} onClick={() => alert(1)} />
        // }}
        onRowClick={(event, rowData) => {
          setSelectedSkuId(rowData.id);
          setManualSku(rowData.sku, rowData.bar_code);
          setIsOpen(false);
        }}
        columns={[
          { title: 'SKU', field: 'sku' },
          { title: 'Barcode', field: 'bar_code' },
          { title: 'Comment', field: 'comment' }
        ]}
        options={{
          pageSize: 10
        }}
        data={sku}
        title="SKU"
      />
    </Dialog>
  );
};

export default DeviceInfoGrid;
